import { useEffect, useState } from "react";
import axios from "axios";
import {
  ShieldCheck, ShieldAlert, Activity, Globe, Database, Zap, Users, Cpu,
  Sparkles, FileText, BookOpen, Heart, ChevronRight,
} from "lucide-react";
import "./TrustCenter.css";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api/public/trust-center`;

function StatusBadge({ status }) {
  return <span className={`hw-trust__status-badge hw-trust__status-badge--${status}`}
                 data-testid={`trust-status-${status}`}>{status}</span>;
}

function fmtNum(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat().format(n);
}

export default function TrustCenter() {
  const [status, setStatus] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [security, setSecurity] = useState(null);
  const [governance, setGovernance] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/status`).then((r) => setStatus(r.data)).catch(() => {}),
      axios.get(`${API}/metrics`).then((r) => setMetrics(r.data)).catch(() => {}),
      axios.get(`${API}/announcements`).then((r) => setAnnouncements(r.data.announcements || [])).catch(() => {}),
      axios.get(`${API}/incidents?limit=10`).then((r) => setIncidents(r.data.incidents || [])).catch(() => {}),
      axios.get(`${API}/maintenance`).then((r) => setMaintenance(r.data.maintenance || [])).catch(() => {}),
      axios.get(`${API}/security-overview`).then((r) => setSecurity(r.data)).catch(() => {}),
      axios.get(`${API}/governance-overview`).then((r) => setGovernance(r.data)).catch(() => {}),
    ]);
  }, []);

  return (
    <div className="hw-trust" data-testid="trust-center">
      <div className="hw-trust__inner">
        <div className="hw-trust__eyebrow">HarborWell Connect · Trust Center</div>
        <h1 className="hw-trust__title">Transparency, Security, & Reliability</h1>
        <p className="hw-trust__lede">
          HarborWell Connect is a nonprofit care management and community
          collaboration platform serving rural and underserved communities.
          This Trust Center exposes our operational posture, security
          controls, governance practices, and community impact in real time.
        </p>

        {/* Identity Security & Governance cross-link (Iter 74.5) */}
        <a
          href="/identity-security"
          data-testid="trust-identity-link"
          className="inline-flex items-center gap-2 mt-3 mb-2 text-sm text-[#1E3A8A] hover:underline"
        >
          Identity Security &amp; Governance dashboard →
        </a>

        {/* Announcements */}
        {announcements.length > 0 && (
          <section className="hw-trust__section" data-testid="trust-announcements">
            <h2><Sparkles className="w-4 h-4" /> Announcements</h2>
            {announcements.map((a) => (
              <div key={a.id} className="hw-trust__note"
                    data-testid={`trust-announcement-${a.id}`}
                    style={{
                      borderLeftColor: a.severity === "critical" ? "#EF4444" :
                                         a.severity === "warning" ? "#F59E0B" : "#14B8A6",
                    }}>
                <strong>{a.title}</strong>
                <div style={{ marginTop: "0.2rem" }}>{a.body}</div>
                <div style={{ fontSize: "0.7rem", opacity: 0.7, marginTop: "0.3rem" }}>
                  Posted {a.published_at?.slice(0, 10)}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Platform Status */}
        <section className="hw-trust__section" data-testid="trust-platform-status">
          <h2><Activity className="w-4 h-4" />
            Platform Status
            {status && <StatusBadge status={status.overall_status} />}
          </h2>
          {status && (
            <>
              <div className="hw-trust__hero" style={{ marginBottom: "1rem" }}>
                <div className="hw-trust__stat">
                  <div className="hw-trust__stat-label">30-day uptime</div>
                  <div className="hw-trust__stat-value" data-testid="trust-uptime-30">{status.uptime_30d_pct}%</div>
                </div>
                <div className="hw-trust__stat">
                  <div className="hw-trust__stat-label">90-day uptime</div>
                  <div className="hw-trust__stat-value">{status.uptime_90d_pct}%</div>
                </div>
                <div className="hw-trust__stat">
                  <div className="hw-trust__stat-label">Open incidents</div>
                  <div className="hw-trust__stat-value">{status.open_incidents?.length ?? 0}</div>
                </div>
                <div className="hw-trust__stat">
                  <div className="hw-trust__stat-label">Upcoming maintenance</div>
                  <div className="hw-trust__stat-value">{status.upcoming_maintenance?.length ?? 0}</div>
                </div>
              </div>
              <div className="hw-trust__comp-grid" data-testid="trust-components">
                {status.components.map((c) => (
                  <div key={c.key} className="hw-trust__comp" data-testid={`trust-comp-${c.key}`}>
                    <div>
                      <div className="hw-trust__comp-label">{c.label}</div>
                      {c.detail && <div className="hw-trust__comp-detail">{c.detail}</div>}
                    </div>
                    <StatusBadge status={c.status} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: "0.75rem", fontSize: "0.7rem", color: "#64748B" }}>
                Last updated: {status.computed_at?.replace("T", " ").slice(0, 19)} UTC
              </div>
            </>
          )}
        </section>

        {/* Community Impact */}
        <section className="hw-trust__section" data-testid="trust-impact">
          <h2><Heart className="w-4 h-4" /> Community Impact</h2>
          {metrics && (
            <div className="hw-trust__hero">
              {[
                ["organizations", "Organizations"], ["community_partners", "Partners"],
                ["clients_served", "Clients Served"], ["referrals", "Referrals"],
                ["care_plans", "Care Plans"], ["volunteers", "Volunteers"],
                ["resources", "Resources"], ["donations", "Donations"],
                ["api_requests", "API Requests"], ["webhook_deliveries", "Webhook Deliveries"],
                ["uptime_pct", "Uptime"],
              ].map(([k, label]) => (
                <div key={k} className="hw-trust__stat" data-testid={`trust-metric-${k}`}>
                  <div className="hw-trust__stat-label">{label}</div>
                  <div className="hw-trust__stat-value">
                    {k === "uptime_pct" ? `${metrics[k]}%` : fmtNum(metrics[k])}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Security Center */}
        <section className="hw-trust__section" data-testid="trust-security">
          <h2><ShieldCheck className="w-4 h-4" /> Security Center</h2>
          {security && (
            <>
              <ul>{security.principles.map((p, i) => <li key={i}>{p}</li>)}</ul>
              <h3 style={{ margin: "0.75rem 0 0.4rem", fontSize: "0.95rem", color: "#F8FAFC" }}>
                Implemented Controls
              </h3>
              <div className="hw-trust__control-grid">
                {security.implemented_controls.map((c) => (
                  <div key={c.key} className="hw-trust__control" data-testid={`trust-ctrl-${c.key}`}>
                    <h4>{c.label}</h4>
                    <p>{c.description}</p>
                  </div>
                ))}
              </div>
              <div className="hw-trust__disclaimer">{security.disclaimer}</div>
            </>
          )}
        </section>

        {/* API Platform */}
        <section className="hw-trust__section" data-testid="trust-api-platform">
          <h2><Cpu className="w-4 h-4" /> API Platform</h2>
          <p>
            HarborWell Connect exposes an enterprise-grade partner API with
            OAuth 2.0 Client Credentials, per-partner scoped keys, and an
            outbound webhook framework with HMAC-SHA256 signing and reliable
            retry + DLQ delivery.
          </p>
          <ul>
            <li><a href="/developer-portal" className="hw-trust__link" data-testid="trust-link-devportal">Developer Portal</a> — auth, webhooks, event catalog, code samples</li>
            <li><a href="/docs" className="hw-trust__link" data-testid="trust-link-swagger">Interactive OpenAPI (Swagger UI)</a></li>
            <li><a href="/redoc" className="hw-trust__link">ReDoc reference</a></li>
            <li>15 outbound webhook events across client, referral, care plan, appointment, organization, resource, volunteer, donation, and service enrollment lifecycles.</li>
          </ul>
        </section>

        {/* Governance */}
        <section className="hw-trust__section" data-testid="trust-governance">
          <h2><BookOpen className="w-4 h-4" /> Governance</h2>
          {governance && (
            <>
              <div className="hw-trust__control-grid">
                {governance.policies.map((p) => (
                  <div key={p.key} className="hw-trust__control" data-testid={`trust-policy-${p.key}`}>
                    <h4>{p.label}</h4>
                    <p>{p.description}</p>
                    {p.link && <a href={p.link} className="hw-trust__link">Learn more →</a>}
                  </div>
                ))}
              </div>
              <h3 style={{ margin: "1rem 0 0.4rem", fontSize: "0.95rem", color: "#F8FAFC" }}>
                {governance.compliance_stance}
              </h3>
              <div className="hw-trust__disclaimer" data-testid="trust-compliance-disclaimer">
                {governance.compliance_disclaimer}
              </div>
              <h3 style={{ margin: "1rem 0 0.4rem", fontSize: "0.95rem", color: "#F8FAFC" }}>
                <Sparkles className="w-3.5 h-3.5" style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.3rem" }} />
                Responsible AI
              </h3>
              <ul>
                {governance.responsible_ai.principles.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
              <h4 style={{ margin: "0.75rem 0 0.35rem", fontSize: "0.85rem", color: "#5EEAD4" }}>Current AI use</h4>
              <ul>
                {governance.responsible_ai.current_use.map((u, i) => (
                  <li key={i}><strong>{u.feature}</strong> — {u.model} · {u.purpose}</li>
                ))}
              </ul>
              <h4 style={{ margin: "0.75rem 0 0.35rem", fontSize: "0.85rem", color: "#5EEAD4" }}>AI governance practices</h4>
              <ul>
                {governance.responsible_ai.governance_practices.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </>
          )}
        </section>

        {/* Incidents */}
        {incidents.length > 0 && (
          <section className="hw-trust__section" data-testid="trust-incidents">
            <h2><ShieldAlert className="w-4 h-4" /> Incident History</h2>
            {incidents.map((i) => (
              <div key={i.id} className="hw-trust__comp" data-testid={`trust-incident-${i.id}`}
                    style={{ flexDirection: "column", alignItems: "flex-start" }}>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <StatusBadge status={i.status === "resolved" ? "operational" : "incident"} />
                  <strong style={{ color: "#F8FAFC" }}>{i.title}</strong>
                </div>
                <div style={{ fontSize: "0.85rem", color: "#CBD5E1", marginTop: "0.25rem" }}>{i.summary}</div>
                <div style={{ fontSize: "0.7rem", color: "#64748B", marginTop: "0.3rem" }}>
                  Started {i.started_at?.slice(0, 10)}
                  {i.resolved_at && ` · Resolved ${i.resolved_at.slice(0, 10)}`}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Maintenance */}
        {maintenance.length > 0 && (
          <section className="hw-trust__section" data-testid="trust-maintenance">
            <h2><Zap className="w-4 h-4" /> Planned Maintenance</h2>
            {maintenance.map((m) => (
              <div key={m.id} className="hw-trust__comp" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                <strong style={{ color: "#F8FAFC" }}>{m.title}</strong>
                <div style={{ fontSize: "0.85rem", color: "#CBD5E1", marginTop: "0.25rem" }}>{m.description}</div>
                <div style={{ fontSize: "0.7rem", color: "#64748B", marginTop: "0.3rem" }}>
                  {m.window_start} → {m.window_end}
                </div>
              </div>
            ))}
          </section>
        )}

        <div style={{ marginTop: "2.5rem", padding: "1rem 0", borderTop: "1px solid #1E293B",
                        color: "#64748B", fontSize: "0.78rem", textAlign: "center" }}>
          HarborWell Connect · Trust Center · Continuously updated · Contact <a href="mailto:contact@harborwell.care" className="hw-trust__link">contact@harborwell.care</a>
        </div>
      </div>
    </div>
  );
}
